/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { AnonymousCoamIdentity } from '../../helpers/auth';
import { getFromLocalStorage, LS_KEYS } from '../../helpers/window';
import { LoginResponse } from '../sagas/authSaga';

const authToken = getFromLocalStorage(LS_KEYS.AUTH_TOKEN);
const anonCOAMIdentity = getFromLocalStorage(LS_KEYS.ANON_COAM_IDENTITY);

export interface AuthState {
  isLoading: boolean;
  isLoggedIn: boolean;
  token: string;
  anonymousCOAM: {
    identity?: AnonymousCoamIdentity;
    isVerifying: boolean;
    error: string;
  };
  login: {
    isLoading: boolean;
    fullError: any;
  };
}

const initialState: AuthState = {
  isLoading: false,
  isLoggedIn: false,
  token: authToken || '',
  anonymousCOAM: {
    identity: anonCOAMIdentity ? JSON.parse(anonCOAMIdentity) as AnonymousCoamIdentity : undefined,
    isVerifying: false,
    error: '',
  },
  login: {
    isLoading: false,
    fullError: null,
  },
};

/**
 * Authentication slice
 */
export const { reducer, actions } = createSlice({
  name: 'auth',
  initialState: initialState as AuthState,
  reducers: {
    authenticate: (_state, _action: PayloadAction<string | undefined>) => { },
    authRequest: (state) => {
      state.isLoading = true;
    },
    authSuccess: (state, { payload }) => {
      const { isLoggedIn, token } = payload;

      state.isLoading = false;
      state.isLoggedIn = isLoggedIn;
      state.token = token;
    },
    authFailure: (state) => {
      state.isLoading = false;
    },
    validateAnonymousCOAMIdentity: (state) => {
      state.anonymousCOAM.isVerifying = true;
      state.anonymousCOAM.error = '';
    },
    validateAnonymousCOAMIdentitySuccess: (state, action: PayloadAction<AnonymousCoamIdentity>) => {
      state.anonymousCOAM = {
        identity: action.payload,
        isVerifying: false,
        error: '',
      };
    },
    validateAnonymousCOAMIdentityFailure: (state, { payload }) => {
      state.anonymousCOAM = {
        identity: undefined,
        isVerifying: false,
        error: payload.error,
      };
    },
    login: (_state, _action: PayloadAction<LoginResponse>) => { },
    loginRequest: (state) => {
      state.login.isLoading = true;
      state.login.fullError = null;
    },
    loginSuccess: (state, { payload }) => {
      state.isLoggedIn = true;
      state.token = payload.token;
      state.login.isLoading = false;
      state.login.fullError = null;
    },
    loginFailure: (state, { payload }) => {
      state.login.isLoading = false;
      state.login.fullError = payload.fullError;
    },
    logout: () => { },
    logoutRequest: () => { },
    logoutSuccess: (state, { payload }) => {
      state.isLoggedIn = false;
      state.token = payload.token; // Anonymous token
    },
    logoutFailure: () => { },
  },
});

/**
 * Selectors
 */
export const selectors = {
  getAuthToken: ({ auth }: RootState) => auth.token || '',
  getAnonymousCOAMToken: ({ auth }: RootState) => auth.anonymousCOAM?.identity?.anonymousToken,
  getIsAnonymousCOAMVerifying: ({ auth }: RootState) => auth.anonymousCOAM.isVerifying,
  isLoggedIn: ({ auth }: RootState) => auth.isLoggedIn,
  isAuthLoading: ({ auth }: RootState) => auth.isLoading || auth.login.isLoading,
  getLoginFullError: ({ auth }: RootState) => auth.login.fullError,
};
